import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Users, FileText, LayoutGrid, CheckCircle, XCircle,
  Clock, Plus, Pencil, Trash2, ShieldCheck, Building2, Loader2,
  Mail, MailCheck, UserCheck, AlertCircle, Globe, RefreshCw, CalendarClock, Star, Shield, Award,
  TrendingUp, BarChart2, DollarSign, Activity, Layers, Package, Upload, Download,
  FileSpreadsheet, AlertTriangle, ChevronDown, ChevronUp, SkipForward,
  Gift, Coins, Link2, ToggleLeft, ToggleRight, Settings2
} from "lucide-react";
import axios from "axios";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";
import BulkUploadSection from "@/components/BulkUploadSection";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// ── Gift Codes Admin Panel ────────────────────────────────────────────────────
const GiftCodesAdmin = ({ API }) => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ code: "", type: "credits", credits: 50, duration_days: 7, function_ids: [], description: "", max_uses: "", per_user_limit: 1, expires_at: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const fetchCodes = () => {
    setLoading(true);
    axios.get(`${API}/admin/gift-codes`, { withCredentials: true })
      .then(r => setCodes(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(fetchCodes, [API]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await axios.post(`${API}/admin/gift-codes`, {
        ...form,
        max_uses: form.max_uses ? parseInt(form.max_uses) : null,
        credits: parseInt(form.credits) || 0,
        duration_days: parseInt(form.duration_days) || 0,
        expires_at: form.expires_at || null,
      }, { withCredentials: true });
      setShowForm(false);
      setForm({ code: "", type: "credits", credits: 50, duration_days: 7, function_ids: [], description: "", max_uses: "", per_user_limit: 1, expires_at: "" });
      fetchCodes();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to create code");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (codeId) => {
    await axios.patch(`${API}/admin/gift-codes/${codeId}`, {}, { withCredentials: true });
    fetchCodes();
  };

  const [gcConfirm, setGcConfirm] = useState({ open: false, codeId: null });

  const handleDelete = (codeId) => setGcConfirm({ open: true, codeId });

  const doDelete = async () => {
    await axios.delete(`${API}/admin/gift-codes/${gcConfirm.codeId}`, { withCredentials: true });
    setGcConfirm({ open: false, codeId: null });
    fetchCodes();
  };

  return (
    <div className="space-y-6" data-testid="gift-codes-admin">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-semibold text-slate-900 flex items-center gap-2"><Gift size={16} className="text-amber-500" /> Gift Codes</h2>
          <p className="text-sm text-slate-500 mt-0.5">Create and manage promotional codes</p>
        </div>
        <Button onClick={() => setShowForm(v => !v)} size="sm" className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="create-gift-code-btn">
          <Plus size={14} className="mr-1.5" /> {showForm ? "Cancel" : "New Code"}
        </Button>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="bg-amber-50 border border-amber-200 rounded-xl p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Code (leave blank to auto-generate)</Label>
              <Input value={form.code} onChange={e => setForm({...form, code: e.target.value.toUpperCase()})}
                placeholder="GC-PROMO2024" className="mt-1 font-mono uppercase" data-testid="gc-code-input" />
            </div>
            <div>
              <Label className="text-xs">Type</Label>
              <select value={form.type} onChange={e => setForm({...form, type: e.target.value})}
                className="w-full mt-1 px-3 py-2 text-sm border border-slate-200 rounded-md bg-white" data-testid="gc-type-select">
                <option value="credits">Credits</option>
                <option value="time">Time-based Access</option>
                <option value="function">Function Access</option>
              </select>
            </div>
          </div>
          {form.type === "credits" && (
            <div>
              <Label className="text-xs">Credits to Grant</Label>
              <Input type="number" min="1" value={form.credits} onChange={e => setForm({...form, credits: e.target.value})}
                className="mt-1 w-32" data-testid="gc-credits-input" />
            </div>
          )}
          {form.type === "time" && (
            <div>
              <Label className="text-xs">Duration (days)</Label>
              <Input type="number" min="1" value={form.duration_days} onChange={e => setForm({...form, duration_days: e.target.value})}
                className="mt-1 w-32" data-testid="gc-duration-input" />
            </div>
          )}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label className="text-xs">Max Uses (blank = unlimited)</Label>
              <Input type="number" min="1" value={form.max_uses} onChange={e => setForm({...form, max_uses: e.target.value})}
                placeholder="Unlimited" className="mt-1" data-testid="gc-max-uses-input" />
            </div>
            <div>
              <Label className="text-xs">Per-user Limit</Label>
              <Input type="number" min="1" value={form.per_user_limit} onChange={e => setForm({...form, per_user_limit: parseInt(e.target.value)})}
                className="mt-1 w-20" />
            </div>
            <div>
              <Label className="text-xs">Expires At (optional)</Label>
              <Input type="datetime-local" value={form.expires_at} onChange={e => setForm({...form, expires_at: e.target.value})}
                className="mt-1" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Input value={form.description} onChange={e => setForm({...form, description: e.target.value})}
              placeholder="e.g. Holiday promotion 2025" className="mt-1" />
          </div>
          {error && <p className="text-red-500 text-xs">{error}</p>}
          <Button type="submit" disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="gc-save-btn">
            {saving ? "Creating..." : "Create Code"}
          </Button>
        </form>
      )}

      {loading ? (
        <div className="text-center py-8 text-slate-400 text-sm">Loading...</div>
      ) : codes.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-slate-200 rounded-xl">
          <Gift size={28} className="mx-auto mb-2 text-slate-300" />
          <p className="text-slate-500 font-medium">No gift codes yet</p>
          <p className="text-xs text-slate-400 mt-1">Create your first promo code above</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {["Code","Type","Value","Uses","Expires","Status",""].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {codes.map(c => (
                <tr key={c.code_id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-mono font-semibold text-slate-900">{c.code}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      c.type === "credits" ? "bg-amber-100 text-amber-700"
                      : c.type === "time" ? "bg-blue-100 text-blue-700"
                      : "bg-purple-100 text-purple-700"
                    }`}>{c.type}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    {c.type === "credits" ? `${c.credits} cr` : c.type === "time" ? `${c.duration_days}d` : `${(c.function_ids||[]).length} fn`}
                  </td>
                  <td className="px-4 py-3 text-slate-500">{c.used_count}/{c.max_uses ?? "∞"}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{c.expires_at ? new Date(c.expires_at).toLocaleDateString() : "Never"}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                      {c.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button onClick={() => handleToggle(c.code_id)} className="text-xs text-blue-600 hover:underline" data-testid={`toggle-code-${c.code_id}`}>
                        {c.status === "active" ? "Disable" : "Enable"}
                      </button>
                      <button onClick={() => handleDelete(c.code_id)} className="text-xs text-red-500 hover:underline" data-testid={`delete-code-${c.code_id}`}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AlertDialog open={gcConfirm.open} onOpenChange={open => !open && setGcConfirm({ open: false, codeId: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Gift Code</AlertDialogTitle>
            <AlertDialogDescription>This code will be permanently deleted and can no longer be redeemed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ── Referrals Admin Panel ─────────────────────────────────────────────────────
const ReferralsAdmin = ({ API }) => {
  const [settings, setSettings] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [loadingRefs, setLoadingRefs] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");
  const [form, setForm] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/referral-settings`, { withCredentials: true })
      .then(r => { setSettings(r.data); setForm(r.data); })
      .catch(() => {})
      .finally(() => setLoadingSettings(false));
    axios.get(`${API}/admin/referrals`, { withCredentials: true })
      .then(r => setReferrals(r.data.referrals || []))
      .catch(() => {})
      .finally(() => setLoadingRefs(false));
  }, [API]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/referral-settings`, {
        credits_per_referral: parseInt(form.credits_per_referral),
        max_referral_credits_per_user: form.max_referral_credits_per_user ? parseInt(form.max_referral_credits_per_user) : null,
        enabled: form.enabled,
        tiers: form.tiers,
      }, { withCredentials: true });
      setSettings(res.data);
      setSavedMsg("Settings saved!");
      setTimeout(() => setSavedMsg(""), 3000);
    } catch {}
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="referrals-admin">
      <div>
        <h2 className="font-semibold text-slate-900 flex items-center gap-2"><Users size={16} className="text-blue-600" /> Referral Programme</h2>
        <p className="text-sm text-slate-500 mt-0.5">Configure rewards and view all referrals</p>
      </div>

      {/* Settings form */}
      {form && (
        <form onSubmit={handleSave} className="bg-blue-50 border border-blue-200 rounded-xl p-5 space-y-4">
          <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2"><Settings2 size={14} /> Reward Settings</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Credits per Referral</Label>
              <Input type="number" min="0" value={form.credits_per_referral}
                onChange={e => setForm({...form, credits_per_referral: e.target.value})}
                className="mt-1 w-28" data-testid="credits-per-referral-input" />
            </div>
            <div>
              <Label className="text-xs">Max Credits per User (blank = no cap)</Label>
              <Input type="number" min="0" value={form.max_referral_credits_per_user || ""}
                onChange={e => setForm({...form, max_referral_credits_per_user: e.target.value || null})}
                placeholder="No cap" className="mt-1 w-32" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Label className="text-xs">Programme Enabled</Label>
            <button type="button" onClick={() => setForm({...form, enabled: !form.enabled})}
              className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-all ${form.enabled ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}
              data-testid="referral-enabled-toggle">
              {form.enabled ? "Enabled" : "Disabled"}
            </button>
          </div>
          <div>
            <Label className="text-xs mb-2 block">Milestone Tiers</Label>
            {(form.tiers || []).map((tier, i) => (
              <div key={i} className="flex items-center gap-2 mb-2">
                <span className="text-xs text-slate-500 w-8">#{i+1}</span>
                <Input type="number" min="1" value={tier.referrals}
                  onChange={e => { const t=[...form.tiers]; t[i]={...t[i],referrals:parseInt(e.target.value)}; setForm({...form,tiers:t}); }}
                  className="w-24 text-xs" placeholder="Referrals" />
                <span className="text-xs text-slate-400">refs →</span>
                <Input type="number" min="0" value={tier.bonus_credits}
                  onChange={e => { const t=[...form.tiers]; t[i]={...t[i],bonus_credits:parseInt(e.target.value)}; setForm({...form,tiers:t}); }}
                  className="w-24 text-xs" placeholder="Bonus" />
                <span className="text-xs text-slate-400">bonus cr</span>
                <button type="button" onClick={() => setForm({...form, tiers: form.tiers.filter((_,j)=>j!==i)})}
                  className="text-xs text-red-400 hover:text-red-600">✕</button>
              </div>
            ))}
            <button type="button" onClick={() => setForm({...form, tiers: [...(form.tiers||[]), {referrals: 5, bonus_credits: 50}]})}
              className="text-xs text-blue-600 hover:underline mt-1">+ Add tier</button>
          </div>
          <div className="flex items-center gap-3">
            <Button type="submit" disabled={saving} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-referral-settings-btn">
              {saving ? "Saving..." : "Save Settings"}
            </Button>
            {savedMsg && <span className="text-emerald-600 text-xs font-medium">{savedMsg}</span>}
          </div>
        </form>
      )}

      {/* Referrals list */}
      <div>
        <h3 className="text-sm font-semibold text-slate-800 mb-3">Recent Referrals ({referrals.length})</h3>
        {loadingRefs ? (
          <div className="text-center py-6 text-slate-400 text-sm">Loading...</div>
        ) : referrals.length === 0 ? (
          <div className="text-center py-10 border border-dashed border-slate-200 rounded-xl">
            <Users size={24} className="mx-auto mb-2 text-slate-300" />
            <p className="text-slate-500 text-sm">No referrals yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  {["Referrer Code","Referred Email","Credits Awarded","Date"].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {referrals.map(r => (
                  <tr key={r.referral_id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono text-xs text-slate-700">{r.referrer_code}</td>
                    <td className="px-4 py-3 text-slate-700">{r.referred_email}</td>
                    <td className="px-4 py-3">
                      <span className="flex items-center gap-1 text-blue-600 font-semibold"><Coins size={12} />{r.credits_awarded}</span>
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{new Date(r.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const FUNCTIONS = [
  { id: "procurement", name: "Procurement" },
  { id: "finance",     name: "Finance" },
  { id: "marketing",   name: "Marketing" },
  { id: "hr",          name: "HR" },
  { id: "sales",       name: "Sales" },
  { id: "operations",  name: "Operations" },
  { id: "general",     name: "General" },
];

const statusBadge = {
  submitted:    "bg-yellow-100 text-yellow-700",
  under_review: "bg-blue-100 text-blue-700",
  approved:     "bg-emerald-100 text-emerald-700",
  rejected:     "bg-red-100 text-red-700",
  active:       "bg-emerald-100 text-emerald-700",
  inactive:     "bg-slate-100 text-slate-600",
};

const AdminPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats]         = useState({});
  const [users, setUsers]         = useState([]);
  const [submissions, setSubm]    = useState([]);
  const [apps, setApps]           = useState([]);
  const [corpDomains, setCorpDomains] = useState([]);
  const [contributors, setContributors] = useState([]);
  const [contribUsers, setContribUsers] = useState([]);
  const [contribForm, setContribForm] = useState({ user_id: "", name: "", title: "", bio: "", expertise_areas: [] });
  const [contribSaving, setContribSaving] = useState(false);
  const [contribError, setContribError] = useState("");
  const [pricingData, setPricingData] = useState(null);
  const [pricingEdits, setPricingEdits] = useState({ plans: {}, credit_packs: {} });
  const [pricingSaving, setPricingSaving] = useState(false);
  const [pricingSuccess, setPricingSuccess] = useState(false);
  const [scheduleMode, setScheduleMode] = useState(false);
  const [effectiveAt, setEffectiveAt] = useState("");
  const [cancellingPending, setCancellingPending] = useState(false);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState("");
  const [activeTab, setActiveTab] = useState("overview");

  // ── Shared in-app confirmation dialog (replaces window.confirm) ──────────
  const [confirmState, setConfirmState] = useState({ open: false, message: "", onConfirm: null });
  const confirmAction = useCallback((message, onConfirm) => {
    setConfirmState({ open: true, message, onConfirm });
  }, []);

  // Domain form state
  const [domainForm, setDomainForm] = useState({ domain: "", plan: "full", notes: "", custom_credits: "" });
  const [domainSaving, setDomainSaving] = useState(false);
  const [domainError, setDomainError] = useState("");
  const [domainFeedback, setDomainFeedback] = useState({}); // { domain_id: "applying"|"applied"|"error" }

  // Corporate user provisioning
  const [corpUserForm, setCorpUserForm] = useState({ user_id: "", plan: "full", custom_credits: "" });
  const [corpUserSaving, setCorpUserSaving] = useState(false);
  const [corpUserError, setCorpUserError] = useState("");
  const [corpUserSuccess, setCorpUserSuccess] = useState("");

  // Analytics
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsDays, setAnalyticsDays] = useState(30);

  // App Analytics
  const [appAnalytics, setAppAnalytics] = useState([]);
  const [appAnalyticsLoading, setAppAnalyticsLoading] = useState(false);
  const [appAnalyticsFn, setAppAnalyticsFn] = useState("");
  const [appAnalyticsSort, setAppAnalyticsSort] = useState("view_count");
  const [appAnalyticsSearch, setAppAnalyticsSearch] = useState("");

  // Subcategories admin state
  const [subcategories, setSubcategories] = useState([]);
  const [subcatForm, setSubcatForm] = useState({ function_id: "", name: "", description: "", order: 0 });
  const [editSubcat, setEditSubcat] = useState(null);
  const [subcatSaving, setSubcatSaving] = useState(false);
  const [subcatError, setSubcatError] = useState("");
  const [subcatFilterFn, setSubcatFilterFn] = useState("");

  // Bundles admin state
  const [adminBundles, setAdminBundles] = useState([]);
  const [bundleForm, setBundleForm] = useState({ name: "", description: "", function_id: "", is_premium: false, tags: "", app_ids: [] });
  const [editBundle, setEditBundle] = useState(null);
  const [bundleSaving, setBundleSaving] = useState(false);
  const [bundleError, setBundleError] = useState("");
  const [allAppsForBundle, setAllAppsForBundle] = useState([]); // lightweight list

  // App Create/Edit State
  const [showAppForm, setShowAppForm] = useState(false);
  const [editApp, setEditApp] = useState(null);
  const [appForm, setAppForm] = useState({ title: "", function_id: "", category: "", subcategory_id: "", business_context: "", when_to_use: "", input_required: "", main_prompt: "", expected_output: "", pro_tips: [], recommended_tools: [], is_premium: false, credit_cost: 0, tags: [], status: "active" });
  const [saving, setSaving] = useState(false);

  // User verification state
  const [showUnverifiedOnly, setShowUnverifiedOnly] = useState(false);
  const [actionLoading, setActionLoading] = useState({});   // { user_id: 'verify'|'resend' }
  const [actionFeedback, setActionFeedback] = useState({}); // { user_id: 'verified'|'sent'|'error'|'no_email' }

  useEffect(() => {
    if (!user?.is_admin) return;
    fetchAll();
  }, [user]);

  const fetchAnalytics = async (days = analyticsDays) => {
    setAnalyticsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/analytics?days=${days}`, { withCredentials: true });
      setAnalytics(res.data);
    } catch {
      setError("Failed to load analytics.");
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const fetchAppAnalytics = async (fn = appAnalyticsFn, sort = appAnalyticsSort) => {
    setAppAnalyticsLoading(true);
    try {
      const params = new URLSearchParams({ sort_by: sort, limit: 200 });
      if (fn) params.append("function_id", fn);
      const res = await axios.get(`${API}/admin/app-analytics?${params}`, { withCredentials: true });
      setAppAnalytics(res.data);
    } catch {
      toast.error("Failed to load app analytics.");
    } finally {
      setAppAnalyticsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "analytics" && user?.is_admin) {
      if (!analytics) fetchAnalytics();
      if (appAnalytics.length === 0) fetchAppAnalytics();
    }
    if ((activeTab === "subcategories" || activeTab === "apps") && user?.is_admin && subcategories.length === 0) {
      fetchSubcategories();
    }
    if (activeTab === "bundles" && user?.is_admin && adminBundles.length === 0) {
      fetchAdminBundles();
    }
  }, [activeTab]);

  const fetchSubcategories = async () => {
    try {
      const res = await axios.get(`${API}/admin/subcategories`, { withCredentials: true });
      setSubcategories(res.data);
    } catch { setError("Failed to load subcategories."); }
  };

  const fetchAdminBundles = async () => {
    try {
      const [bundlesRes, appsRes] = await Promise.all([
        axios.get(`${API}/admin/bundles`, { withCredentials: true }),
        axios.get(`${API}/admin/apps-list`, { withCredentials: true }),
      ]);
      setAdminBundles(bundlesRes.data);
      setAllAppsForBundle(appsRes.data);
    } catch { setError("Failed to load bundles."); }
  };

  const handleSaveSubcat = async (e) => {
    e.preventDefault();
    setSubcatError("");
    setSubcatSaving(true);
    try {
      if (editSubcat) {
        const res = await axios.put(`${API}/admin/subcategories/${editSubcat.subcategory_id}`, subcatForm, { withCredentials: true });
        setSubcategories(prev => prev.map(s => s.subcategory_id === editSubcat.subcategory_id ? { ...s, ...res.data } : s));
      } else {
        const res = await axios.post(`${API}/admin/subcategories`, subcatForm, { withCredentials: true });
        setSubcategories(prev => [...prev, res.data]);
      }
      setSubcatForm({ function_id: "", name: "", description: "", order: 0 });
      setEditSubcat(null);
    } catch (err) {
      setSubcatError(err.response?.data?.detail || "Failed to save subcategory.");
    } finally { setSubcatSaving(false); }
  };

  const handleDeleteSubcat = (subcatId) => {
    confirmAction("Delete this subcategory? Apps in it will become unassigned.", async () => {
      try {
        await axios.delete(`${API}/admin/subcategories/${subcatId}`, { withCredentials: true });
        setSubcategories(prev => prev.filter(s => s.subcategory_id !== subcatId));
      } catch { setError("Failed to delete subcategory."); }
    });
  };

  const handleSaveBundle = async (e) => {
    e.preventDefault();
    setBundleError("");
    setBundleSaving(true);
    try {
      const payload = {
        ...bundleForm,
        tags: bundleForm.tags ? bundleForm.tags.split(",").map(t => t.trim()).filter(Boolean) : [],
      };
      if (editBundle) {
        const res = await axios.put(`${API}/admin/bundles/${editBundle.bundle_id}`, payload, { withCredentials: true });
        setAdminBundles(prev => prev.map(b => b.bundle_id === editBundle.bundle_id ? { ...res.data, app_count: res.data.app_ids?.length || 0 } : b));
      } else {
        const res = await axios.post(`${API}/admin/bundles`, payload, { withCredentials: true });
        setAdminBundles(prev => [{ ...res.data, app_count: res.data.app_ids?.length || 0 }, ...prev]);
      }
      setBundleForm({ name: "", description: "", function_id: "", is_premium: false, tags: "", app_ids: [] });
      setEditBundle(null);
    } catch (err) {
      setBundleError(err.response?.data?.detail || "Failed to save bundle.");
    } finally { setBundleSaving(false); }
  };

  const handleDeleteBundle = (bundleId) => {
    confirmAction("Delete this bundle? This cannot be undone.", async () => {
      try {
        await axios.delete(`${API}/admin/bundles/${bundleId}`, { withCredentials: true });
        setAdminBundles(prev => prev.filter(b => b.bundle_id !== bundleId));
      } catch { setError("Failed to delete bundle."); }
    });
  };

  const toggleBundleApp = (appId) => {
    setBundleForm(f => ({
      ...f,
      app_ids: f.app_ids.includes(appId) ? f.app_ids.filter(id => id !== appId) : [...f.app_ids, appId],
    }));
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [statsR, usersR, submR, appsR, domainsR, pricingR, contribR] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/admin/submissions`, { withCredentials: true }),
        axios.get(`${API}/admin/apps`, { withCredentials: true }),
        axios.get(`${API}/admin/corporate-domains`, { withCredentials: true }),
        axios.get(`${API}/admin/pricing`, { withCredentials: true }),
        axios.get(`${API}/admin/contributors`, { withCredentials: true }),
      ]);
      setStats(statsR.data);
      setUsers(usersR.data);
      setSubm(submR.data);
      setApps(appsR.data);
      setCorpDomains(domainsR.data);
      setContributors(contribR.data);
      // users without contributor profile (for the "make contributor" selector)
      const existingUserIds = new Set(contribR.data.filter(c => c.user_id).map(c => c.user_id));
      setContribUsers(usersR.data.filter(u => !u.is_admin && !existingUserIds.has(u.user_id)));
      setPricingData(pricingR.data);
      // Seed edits with current values
      const plans = {};
      Object.entries(pricingR.data.plans).forEach(([k, v]) => {
        if (k !== "free") plans[k] = { price_eur: v.price_eur, credits: v.credits };
      });
      const packs = {};
      Object.entries(pricingR.data.credit_packs).forEach(([k, v]) => {
        packs[k] = { amount: v.amount, credits: v.credits };
      });
      setPricingEdits({ plans, credit_packs: packs });
    } catch (err) {
      setError("Failed to load admin data.");
    } finally {
      setLoading(false);
    }
  };

  const reviewSubmission = async (subId, status) => {
    try {
      await axios.put(`${API}/admin/submissions/${subId}`, { status }, { withCredentials: true });
      setSubm(prev => prev.map(s => s.sub_id === subId ? { ...s, status } : s));
    } catch { setError("Review failed."); }
  };

  const deleteApp = (appId) => {
    confirmAction("Delete this application? This cannot be undone.", async () => {
      try {
        await axios.delete(`${API}/admin/apps/${appId}`, { withCredentials: true });
        setApps(prev => prev.filter(a => a.app_id !== appId));
      } catch { setError("Delete failed."); }
    });
  };

  const saveApp = async () => {
    setSaving(true);
    try {
      if (editApp) {
        const res = await axios.put(`${API}/admin/apps/${editApp.app_id}`, appForm, { withCredentials: true });
        setApps(prev => prev.map(a => a.app_id === editApp.app_id ? res.data : a));
      } else {
        const res = await axios.post(`${API}/admin/apps`, appForm, { withCredentials: true });
        setApps(prev => [res.data, ...prev]);
      }
      setShowAppForm(false);
      setEditApp(null);
    } catch (err) {
      setError(err.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const openEditApp = (app) => {
    setEditApp(app);
    setAppForm({ ...app, pro_tips: app.pro_tips || [], recommended_tools: app.recommended_tools || [], tags: app.tags || [], subcategory_id: app.subcategory_id || "" });
    setShowAppForm(true);
  };

  const handleVerifyUser = async (userId) => {
    setActionLoading(prev => ({ ...prev, [userId]: "verify" }));
    try {
      await axios.post(`${API}/admin/users/${userId}/verify`, {}, { withCredentials: true });
      setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, email_verified: true } : u));
      setActionFeedback(prev => ({ ...prev, [userId]: "verified" }));
      setTimeout(() => setActionFeedback(prev => { const n = { ...prev }; delete n[userId]; return n; }), 3000);
    } catch {
      setActionFeedback(prev => ({ ...prev, [userId]: "error" }));
      setTimeout(() => setActionFeedback(prev => { const n = { ...prev }; delete n[userId]; return n; }), 3000);
    } finally {
      setActionLoading(prev => { const n = { ...prev }; delete n[userId]; return n; });
    }
  };

  const handleResendVerification = async (userId) => {
    setActionLoading(prev => ({ ...prev, [userId]: "resend" }));
    try {
      await axios.post(`${API}/admin/users/${userId}/resend-verification`, {}, { withCredentials: true });
      setActionFeedback(prev => ({ ...prev, [userId]: "sent" }));
      setTimeout(() => setActionFeedback(prev => { const n = { ...prev }; delete n[userId]; return n; }), 3000);
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      setActionFeedback(prev => ({ ...prev, [userId]: detail.includes("configured") ? "no_email" : "error" }));
      setTimeout(() => setActionFeedback(prev => { const n = { ...prev }; delete n[userId]; return n; }), 4000);
    } finally {
      setActionLoading(prev => { const n = { ...prev }; delete n[userId]; return n; });
    }
  };

  const handleAddDomain = async (e) => {
    e.preventDefault();
    setDomainError("");
    setDomainSaving(true);
    try {
      const payload = {
        ...domainForm,
        custom_credits: domainForm.custom_credits !== "" ? parseInt(domainForm.custom_credits) : null,
      };
      const res = await axios.post(`${API}/admin/corporate-domains`, payload, { withCredentials: true });
      setCorpDomains(prev => [res.data, ...prev]);
      setDomainForm({ domain: "", plan: "full", notes: "", custom_credits: "" });
    } catch (err) {
      setDomainError(err.response?.data?.detail || "Failed to add domain.");
    } finally {
      setDomainSaving(false);
    }
  };

  const handleDeleteDomain = (domainId) => {
    confirmAction("Remove this domain? Existing provisioned users will keep their current plan.", async () => {
      try {
        await axios.delete(`${API}/admin/corporate-domains/${domainId}`, { withCredentials: true });
        setCorpDomains(prev => prev.filter(d => d.domain_id !== domainId));
      } catch { setError("Failed to remove domain."); }
    });
  };

  const handleProvisionUser = async (e) => {
    e.preventDefault();
    setCorpUserError("");
    setCorpUserSuccess("");
    setCorpUserSaving(true);
    try {
      const payload = {
        subscription_plan: corpUserForm.plan,
        credits: corpUserForm.custom_credits !== "" ? parseInt(corpUserForm.custom_credits) : null,
      };
      const res = await axios.post(`${API}/admin/users/${corpUserForm.user_id}/set-plan`, payload, { withCredentials: true });
      // Update users list with new plan data
      setUsers(prev => prev.map(u => u.user_id === corpUserForm.user_id ? { ...u, ...res.data } : u));
      const provisionedUser = users.find(u => u.user_id === corpUserForm.user_id);
      setCorpUserSuccess(`${provisionedUser?.name || "User"} upgraded to ${corpUserForm.plan} plan successfully.`);
      setCorpUserForm({ user_id: "", plan: "full", custom_credits: "" });
      setTimeout(() => setCorpUserSuccess(""), 5000);
    } catch (err) {
      setCorpUserError(err.response?.data?.detail || "Failed to provision user.");
    } finally {
      setCorpUserSaving(false);
    }
  };

  const handleSavePricing = async () => {
    setPricingSaving(true);
    setPricingSuccess(false);
    try {
      const payload = { ...pricingEdits };
      if (scheduleMode && effectiveAt) {
        payload.effective_at = new Date(effectiveAt).toISOString();
      }
      const res = await axios.put(`${API}/admin/pricing`, payload, { withCredentials: true });
      setPricingData(res.data);
      setPricingSuccess(scheduleMode ? "scheduled" : "saved");
      if (!scheduleMode) {
        // refresh edits with new live values
        const plans = {};
        Object.entries(res.data.plans).forEach(([k, v]) => {
          if (k !== "free") plans[k] = { price_eur: v.price_eur, credits: v.credits };
        });
        const packs = {};
        Object.entries(res.data.credit_packs).forEach(([k, v]) => {
          packs[k] = { amount: v.amount, credits: v.credits };
        });
        setPricingEdits({ plans, credit_packs: packs });
      }
      setTimeout(() => setPricingSuccess(false), 4000);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to save pricing.");
    } finally {
      setPricingSaving(false);
    }
  };

  const handleCreateContributor = async (e) => {
    e.preventDefault();
    setContribError("");
    setContribSaving(true);
    try {
      const res = await axios.post(`${API}/admin/contributors`, contribForm, { withCredentials: true });
      setContributors(prev => [res.data, ...prev]);
      setContribUsers(prev => prev.filter(u => u.user_id !== contribForm.user_id));
      setContribForm({ user_id: "", name: "", title: "", bio: "", expertise_areas: [] });
    } catch (err) {
      setContribError(err.response?.data?.detail || "Failed to create contributor.");
    } finally {
      setContribSaving(false);
    }
  };

  const handleDeleteContributor = (contributorId) => {
    confirmAction("Remove this contributor? Their apps will be reassigned to Promptlee.", async () => {
      try {
        await axios.delete(`${API}/admin/contributors/${contributorId}`, { withCredentials: true });
        setContributors(prev => prev.filter(c => c.contributor_id !== contributorId));
      } catch { setError("Failed to remove contributor."); }
    });
  };

  const handleCancelPending = () => {
    confirmAction("Cancel the scheduled pricing change? This cannot be undone.", async () => {
      setCancellingPending(true);
      try {
        await axios.delete(`${API}/admin/pricing/pending`, { withCredentials: true });
        setPricingData(prev => ({ ...prev, pending: null }));
      } catch {
        setError("Failed to cancel scheduled pricing.");
      } finally { setCancellingPending(false); }
    });
  };
  const handleApplyDomain = async (domainId) => {
    setDomainFeedback(prev => ({ ...prev, [domainId]: "applying" }));
    try {
      const res = await axios.post(`${API}/admin/corporate-domains/${domainId}/apply`, {}, { withCredentials: true });
      setCorpDomains(prev => prev.map(d =>
        d.domain_id === domainId ? { ...d, provisioned_count: res.data.applied_to } : d
      ));
      setDomainFeedback(prev => ({ ...prev, [domainId]: `Applied to ${res.data.applied_to} user(s)` }));
      setTimeout(() => setDomainFeedback(prev => { const n = { ...prev }; delete n[domainId]; return n; }), 4000);
    } catch {
      setDomainFeedback(prev => ({ ...prev, [domainId]: "error" }));
      setTimeout(() => setDomainFeedback(prev => { const n = { ...prev }; delete n[domainId]; return n; }), 3000);
    }
  };

  if (!user?.is_admin) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <ShieldCheck size={40} className="text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 mb-4">Admin access required</p>
          <Button onClick={() => navigate("/")} variant="outline">Go Home</Button>
        </div>
      </div>
    );
  }

  const StatCard = ({ label, value, icon: Icon, color }) => (
    <div className="bg-white border border-slate-200 rounded-xl p-5 flex items-center gap-4">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
        <Icon size={18} />
      </div>
      <div>
        <p className="text-2xl font-bold text-slate-900">{value}</p>
        <p className="text-xs text-slate-500">{label}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-3 mb-8">
          <ShieldCheck size={24} className="text-blue-600" />
          <h1 className="text-2xl font-bold text-slate-900">Admin Panel</h1>
        </div>

        {error && <Alert variant="destructive" className="mb-4"><AlertDescription>{error}</AlertDescription></Alert>}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 flex flex-wrap gap-1 h-auto">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="apps">Applications</TabsTrigger>
            <TabsTrigger value="submissions">Submissions</TabsTrigger>
            <TabsTrigger value="users">Users</TabsTrigger>
            <TabsTrigger value="enterprise">Enterprise Access</TabsTrigger>
            <TabsTrigger value="pricing">Pricing</TabsTrigger>
            <TabsTrigger value="contributors">Contributors</TabsTrigger>
            <TabsTrigger value="subcategories" data-testid="subcategories-tab">Subcategories</TabsTrigger>
            <TabsTrigger value="bulk-upload" data-testid="bulk-upload-tab">Bulk Upload</TabsTrigger>
            <TabsTrigger value="gift-codes" data-testid="gift-codes-tab">Gift Codes</TabsTrigger>
            <TabsTrigger value="promotions" data-testid="promotions-tab">Referrals</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="analytics-tab">Analytics</TabsTrigger>
          </TabsList>

          {/* Overview */}
          <TabsContent value="overview">
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
              <StatCard label="Total Users"         value={stats.users ?? "—"}                icon={Users}       color="bg-blue-100 text-blue-600" />
              <StatCard label="Unverified Users"     value={stats.unverified_users ?? "—"}     icon={Clock}       color="bg-amber-100 text-amber-600" />
              <StatCard label="Active Apps"          value={stats.apps ?? "—"}                 icon={LayoutGrid}  color="bg-emerald-100 text-emerald-600" />
              <StatCard label="Pending Submissions"  value={stats.pending_submissions ?? "—"}  icon={FileText}    color="bg-violet-100 text-violet-600" />
              <StatCard label="Paid Transactions"    value={stats.paid_transactions ?? "—"}    icon={CheckCircle} color="bg-teal-100 text-teal-600" />
            </div>
          </TabsContent>

          {/* Applications */}
          <TabsContent value="apps">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-semibold text-slate-900">Applications ({apps.length})</h2>
              <Button onClick={async () => {
                setEditApp(null);
                setAppForm({ title: "", function_id: "", category: "", subcategory_id: "", business_context: "", when_to_use: "", input_required: "", main_prompt: "", expected_output: "", pro_tips: [], recommended_tools: [], is_premium: false, credit_cost: 0, tags: [], status: "active" });
                setShowAppForm(true);
                if (subcategories.length === 0) {
                  const res = await axios.get(`${API}/admin/subcategories`, { withCredentials: true });
                  setSubcategories(res.data);
                }
              }} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-app-btn">
                <Plus size={16} className="mr-1.5" /> Add App
              </Button>
            </div>

            {showAppForm && (
              <div className="bg-white border border-slate-200 rounded-xl p-6 mb-4">
                <h3 className="font-semibold mb-4">{editApp ? "Edit Application" : "New Application"}</h3>
                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div><Label>Title</Label><Input value={appForm.title} onChange={e => setAppForm(f => ({ ...f, title: e.target.value }))} className="mt-1" data-testid="admin-app-title-input" /></div>
                  <div>
                    <Label>Function</Label>
                    <Select value={appForm.function_id} onValueChange={v => setAppForm(f => ({ ...f, function_id: v, subcategory_id: "" }))}>
                      <SelectTrigger className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{FUNCTIONS.map(fn => <SelectItem key={fn.id} value={fn.id}>{fn.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Category <span className="text-slate-400 font-normal text-xs">(display label)</span></Label><Input value={appForm.category} onChange={e => setAppForm(f => ({ ...f, category: e.target.value }))} className="mt-1" /></div>
                  <div>
                    <Label>Subcategory <span className="text-slate-400 font-normal text-xs">(navigation)</span></Label>
                    <Select value={appForm.subcategory_id || "none"} onValueChange={v => setAppForm(f => ({ ...f, subcategory_id: v === "none" ? "" : v }))}>
                      <SelectTrigger className="mt-1" data-testid="admin-app-subcategory-select">
                        <SelectValue placeholder="Assign to subcategory..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No subcategory</SelectItem>
                        {subcategories
                          .filter(s => !appForm.function_id || s.function_id === appForm.function_id)
                          .map(s => <SelectItem key={s.subcategory_id} value={s.subcategory_id}>{s.name}</SelectItem>)
                        }
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-4 pt-5">
                    <div className="flex items-center gap-2">
                      <Switch checked={appForm.is_premium} onCheckedChange={v => setAppForm(f => ({ ...f, is_premium: v }))} />
                      <Label>Premium</Label>
                    </div>
                    {appForm.is_premium && (
                      <div className="flex items-center gap-2">
                        <Label>Credits:</Label>
                        <Input type="number" value={appForm.credit_cost} onChange={e => setAppForm(f => ({ ...f, credit_cost: parseInt(e.target.value) }))} className="w-20" />
                      </div>
                    )}
                  </div>
                </div>
                <div className="space-y-3 mb-4">
                  <div><Label>Business Context</Label><Textarea value={appForm.business_context} onChange={e => setAppForm(f => ({ ...f, business_context: e.target.value }))} rows={2} className="mt-1" /></div>
                  <div><Label>When to Use</Label><Textarea value={appForm.when_to_use} onChange={e => setAppForm(f => ({ ...f, when_to_use: e.target.value }))} rows={2} className="mt-1" /></div>
                  <div><Label>Input Required</Label><Input value={appForm.input_required} onChange={e => setAppForm(f => ({ ...f, input_required: e.target.value }))} className="mt-1" /></div>
                  <div><Label>Main Prompt</Label><Textarea value={appForm.main_prompt} onChange={e => setAppForm(f => ({ ...f, main_prompt: e.target.value }))} rows={6} className="mt-1 font-mono text-sm" /></div>
                  <div><Label>Expected Output</Label><Textarea value={appForm.expected_output} onChange={e => setAppForm(f => ({ ...f, expected_output: e.target.value }))} rows={2} className="mt-1" /></div>
                </div>
                <div className="flex gap-3">
                  <Button onClick={saveApp} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-app-btn">
                    {saving ? <Loader2 className="animate-spin w-4 h-4 mr-1" /> : null}
                    {editApp ? "Save Changes" : "Create App"}
                  </Button>
                  <Button variant="outline" onClick={() => { setShowAppForm(false); setEditApp(null); }}>Cancel</Button>
                </div>
              </div>
            )}

            <div className="space-y-2">
              {apps.map(app => (
                <div key={app.app_id} className="bg-white border border-slate-100 rounded-lg px-4 py-3 flex items-center justify-between gap-3" data-testid={`admin-app-row-${app.app_id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 text-sm truncate">{app.title}</p>
                    <p className="text-xs text-slate-500">{app.function_id} • {app.category}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Badge className={`text-xs border-0 ${app.is_premium ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                      {app.is_premium ? "Premium" : "Free"}
                    </Badge>
                    <Badge className={`text-xs border-0 ${statusBadge[app.status]}`}>{app.status}</Badge>
                    <button onClick={() => openEditApp(app)} className="p-1.5 hover:bg-slate-100 rounded" data-testid={`edit-app-${app.app_id}`}><Pencil size={13} className="text-slate-500" /></button>
                    <button onClick={() => deleteApp(app.app_id)} className="p-1.5 hover:bg-red-50 rounded" data-testid={`delete-app-${app.app_id}`}><Trash2 size={13} className="text-red-400" /></button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Submissions */}
          <TabsContent value="submissions">
            <h2 className="font-semibold text-slate-900 mb-4">Contributor Submissions ({submissions.length})</h2>
            {submissions.length === 0 ? (
              <p className="text-slate-400 text-sm">No submissions yet.</p>
            ) : (
              <div className="space-y-3">
                {submissions.map(sub => (
                  <div key={sub.sub_id} className="bg-white border border-slate-100 rounded-xl p-5" data-testid={`submission-row-${sub.sub_id}`}>
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div>
                        <p className="font-medium text-slate-900">{sub.title}</p>
                        <p className="text-xs text-slate-500 mt-0.5 capitalize">{sub.function_id} • {sub.category} • by {sub.user_name}</p>
                      </div>
                      <Badge className={`text-xs border-0 flex-shrink-0 ${statusBadge[sub.status]}`}>
                        {sub.status?.replace("_", " ")}
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-600 mb-3 line-clamp-2">{sub.business_context}</p>
                    {sub.status === "submitted" && (
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => reviewSubmission(sub.sub_id, "approved")} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`approve-sub-${sub.sub_id}`}>
                          <CheckCircle size={13} className="mr-1.5" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => reviewSubmission(sub.sub_id, "rejected")} className="border-red-200 text-red-600 hover:bg-red-50" data-testid={`reject-sub-${sub.sub_id}`}>
                          <XCircle size={13} className="mr-1.5" /> Reject
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Users */}
          <TabsContent value="users">
            {/* Stats summary */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              {[
                { label: "Total Users",  value: users.length,                                       color: "text-slate-900" },
                { label: "Verified",     value: users.filter(u => u.email_verified !== false).length, color: "text-emerald-600" },
                { label: "Unverified",   value: users.filter(u => u.email_verified === false).length, color: "text-amber-600" },
              ].map(stat => (
                <div key={stat.label} className="bg-white border border-slate-200 rounded-xl p-3 text-center">
                  <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{stat.label}</p>
                </div>
              ))}
            </div>

            {/* Filter toggle */}
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-slate-900">
                {showUnverifiedOnly
                  ? `Unverified Users (${users.filter(u => u.email_verified === false).length})`
                  : `All Users (${users.length})`}
              </h2>
              <button
                onClick={() => setShowUnverifiedOnly(v => !v)}
                className={`text-xs px-3 py-1.5 rounded-full border font-medium transition-all ${
                  showUnverifiedOnly
                    ? "bg-amber-100 text-amber-700 border-amber-200"
                    : "bg-slate-100 text-slate-600 border-slate-200 hover:border-amber-200 hover:text-amber-600"
                }`}
                data-testid="unverified-filter-toggle"
              >
                {showUnverifiedOnly ? "Showing unverified only" : "Show unverified only"}
              </button>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Name</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600 hidden sm:table-cell">Email</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600 hidden md:table-cell">Plan</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Email Status</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {users
                      .filter(u => !showUnverifiedOnly || u.email_verified === false)
                      .map(u => {
                        const isVerified = u.email_verified !== false;
                        const loading = actionLoading[u.user_id];
                        const feedback = actionFeedback[u.user_id];
                        return (
                          <tr key={u.user_id} className={`hover:bg-slate-50 ${!isVerified ? "bg-amber-50/40" : ""}`} data-testid={`user-row-${u.user_id}`}>
                            <td className="px-4 py-3">
                              <p className="font-medium text-slate-900 text-sm">{u.name || "—"}</p>
                              {u.is_admin && <span className="text-xs text-purple-600 font-medium">Admin</span>}
                            </td>
                            <td className="px-4 py-3 text-slate-500 hidden sm:table-cell text-xs">{u.email}</td>
                            <td className="px-4 py-3 hidden md:table-cell">
                              <Badge className="text-xs border-0 bg-blue-100 text-blue-700 capitalize">{u.subscription_plan}</Badge>
                            </td>
                            <td className="px-4 py-3">
                              {isVerified ? (
                                <span className="inline-flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded-full font-medium" data-testid={`verified-badge-${u.user_id}`}>
                                  <CheckCircle size={11} /> Verified
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 border border-amber-100 px-2 py-0.5 rounded-full font-medium" data-testid={`unverified-badge-${u.user_id}`}>
                                  <Clock size={11} /> Unverified
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              {!isVerified && (
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  {feedback === "verified" ? (
                                    <span className="text-xs text-emerald-600 flex items-center gap-1"><MailCheck size={12} /> Verified!</span>
                                  ) : feedback === "sent" ? (
                                    <span className="text-xs text-blue-600 flex items-center gap-1"><Mail size={12} /> Email sent</span>
                                  ) : feedback === "no_email" ? (
                                    <span className="text-xs text-slate-500 flex items-center gap-1"><AlertCircle size={12} /> No email service</span>
                                  ) : feedback === "error" ? (
                                    <span className="text-xs text-red-500 flex items-center gap-1"><XCircle size={12} /> Failed</span>
                                  ) : (
                                    <>
                                      <button
                                        onClick={() => handleVerifyUser(u.user_id)}
                                        disabled={!!loading}
                                        className="inline-flex items-center gap-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-2.5 py-1 rounded-lg font-medium disabled:opacity-50 transition-all"
                                        data-testid={`verify-user-btn-${u.user_id}`}
                                      >
                                        {loading === "verify" ? <Loader2 size={11} className="animate-spin" /> : <UserCheck size={11} />}
                                        Verify
                                      </button>
                                      <button
                                        onClick={() => handleResendVerification(u.user_id)}
                                        disabled={!!loading}
                                        className="inline-flex items-center gap-1 text-xs border border-slate-200 hover:border-blue-300 hover:text-blue-600 text-slate-500 px-2.5 py-1 rounded-lg font-medium disabled:opacity-50 transition-all bg-white"
                                        data-testid={`resend-verification-btn-${u.user_id}`}
                                      >
                                        {loading === "resend" ? <Loader2 size={11} className="animate-spin" /> : <Mail size={11} />}
                                        Resend
                                      </button>
                                    </>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
                {users.filter(u => !showUnverifiedOnly || u.email_verified === false).length === 0 && (
                  <div className="text-center py-8 text-slate-400">
                    <CheckCircle size={24} className="mx-auto mb-2 text-emerald-500" />
                    <p className="text-sm font-medium text-slate-600">All users are verified</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Corporate */}
          {/* Enterprise Domain Access */}
          <TabsContent value="enterprise">
            <div className="space-y-6">
              {/* Header */}
              <div>
                <h2 className="font-semibold text-slate-900 flex items-center gap-2">
                  <Globe size={16} className="text-blue-600" /> Enterprise Domain Provisioning
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                  Any user who registers with a whitelisted email domain is automatically granted the assigned plan and full function access.
                </p>
              </div>

              {/* Add Domain Form */}
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-slate-900 mb-4">Add Email Domain</h3>
                {domainError && (
                  <Alert variant="destructive" className="mb-3">
                    <AlertDescription>{domainError}</AlertDescription>
                  </Alert>
                )}
                <form onSubmit={handleAddDomain} className="space-y-3">
                  <div className="grid sm:grid-cols-4 gap-3">
                    <div className="sm:col-span-1">
                      <Label>Email Domain</Label>
                      <div className="relative mt-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">@</span>
                        <Input
                          placeholder="acmecorp.com"
                          value={domainForm.domain}
                          onChange={e => setDomainForm(f => ({ ...f, domain: e.target.value }))}
                          required
                          className="pl-7"
                          data-testid="domain-input"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Assign Plan</Label>
                      <Select value={domainForm.plan} onValueChange={v => setDomainForm(f => ({ ...f, plan: v }))}>
                        <SelectTrigger className="mt-1" data-testid="domain-plan-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="single">Single Function (50 credits)</SelectItem>
                          <SelectItem value="multi">Multi-Function (150 credits)</SelectItem>
                          <SelectItem value="full">Full Access (500 credits)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Custom Credits <span className="text-slate-400 font-normal">(optional)</span></Label>
                      <Input
                        type="number"
                        placeholder={`Default: ${domainForm.plan === "full" ? 500 : domainForm.plan === "multi" ? 150 : 50}`}
                        value={domainForm.custom_credits}
                        onChange={e => setDomainForm(f => ({ ...f, custom_credits: e.target.value }))}
                        min={0}
                        className="mt-1"
                        data-testid="domain-credits-input"
                      />
                    </div>
                    <div>
                      <Label>Notes <span className="text-slate-400 font-normal">(optional)</span></Label>
                      <Input
                        placeholder="e.g. Acme Corp enterprise deal"
                        value={domainForm.notes}
                        onChange={e => setDomainForm(f => ({ ...f, notes: e.target.value }))}
                        className="mt-1"
                        data-testid="domain-notes-input"
                      />
                    </div>
                  </div>
                  <Button
                    type="submit"
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    disabled={domainSaving}
                    data-testid="add-domain-btn"
                  >
                    {domainSaving ? <Loader2 className="animate-spin w-4 h-4 mr-1.5" /> : <Plus size={15} className="mr-1.5" />}
                    Add Domain
                  </Button>
                </form>
              </div>

              {/* Provision Individual User */}
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-slate-900 mb-1 flex items-center gap-2">
                  <UserCheck size={15} className="text-blue-600" /> Provision Individual User
                </h3>
                <p className="text-xs text-slate-500 mb-4">Manually assign a corporate plan to a specific user, bypassing the payment flow.</p>
                {corpUserError && <Alert variant="destructive" className="mb-3"><AlertDescription>{corpUserError}</AlertDescription></Alert>}
                {corpUserSuccess && (
                  <div className="mb-3 flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
                    <CheckCircle size={14} /> {corpUserSuccess}
                  </div>
                )}
                <form onSubmit={handleProvisionUser} className="space-y-3">
                  <div className="grid sm:grid-cols-3 gap-3">
                    <div>
                      <Label>Select User</Label>
                      <Select value={corpUserForm.user_id} onValueChange={v => setCorpUserForm(f => ({ ...f, user_id: v }))}>
                        <SelectTrigger className="mt-1" data-testid="corp-user-select">
                          <SelectValue placeholder="Choose a user..." />
                        </SelectTrigger>
                        <SelectContent>
                          {users.filter(u => !u.is_admin).map(u => (
                            <SelectItem key={u.user_id} value={u.user_id}>
                              {u.name} — {u.email}
                              {u.subscription_plan !== "free" && ` (${u.subscription_plan})`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Assign Plan</Label>
                      <Select value={corpUserForm.plan} onValueChange={v => setCorpUserForm(f => ({ ...f, plan: v }))}>
                        <SelectTrigger className="mt-1" data-testid="corp-user-plan-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="single">Single Function (50 credits)</SelectItem>
                          <SelectItem value="multi">Multi-Function (150 credits)</SelectItem>
                          <SelectItem value="full">Full Access (500 credits)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Custom Credits <span className="text-slate-400 font-normal">(optional)</span></Label>
                      <Input
                        type="number"
                        placeholder={`Default: ${corpUserForm.plan === "full" ? 500 : corpUserForm.plan === "multi" ? 150 : 50}`}
                        value={corpUserForm.custom_credits}
                        onChange={e => setCorpUserForm(f => ({ ...f, custom_credits: e.target.value }))}
                        min={0}
                        className="mt-1"
                        data-testid="corp-user-credits-input"
                      />
                    </div>
                  </div>
                  <Button
                    type="submit"
                    disabled={corpUserSaving || !corpUserForm.user_id}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="provision-user-btn"
                  >
                    {corpUserSaving ? <Loader2 className="animate-spin w-4 h-4 mr-1.5" /> : <Building2 size={15} className="mr-1.5" />}
                    Provision User
                  </Button>
                </form>
              </div>

              {/* Corporate Users Table */}
              {users.filter(u => u.subscription_plan && u.subscription_plan !== "free").length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <Building2 size={14} className="text-slate-500" />
                    Corporate / Provisioned Users ({users.filter(u => u.subscription_plan && u.subscription_plan !== "free").length})
                  </h3>
                  <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="text-left px-4 py-3 font-medium text-slate-600">Name</th>
                          <th className="text-left px-4 py-3 font-medium text-slate-600">Email</th>
                          <th className="text-left px-4 py-3 font-medium text-slate-600">Plan</th>
                          <th className="text-left px-4 py-3 font-medium text-slate-600">Credits</th>
                          <th className="text-left px-4 py-3 font-medium text-slate-600">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {users
                          .filter(u => u.subscription_plan && u.subscription_plan !== "free")
                          .map(u => {
                            const planColors = { full: "bg-blue-100 text-blue-700", multi: "bg-violet-100 text-violet-700", single: "bg-emerald-100 text-emerald-700" };
                            return (
                              <tr key={u.user_id} className="hover:bg-slate-50" data-testid={`corp-user-row-${u.user_id}`}>
                                <td className="px-4 py-3 font-medium text-slate-900 text-sm">{u.name || "—"}</td>
                                <td className="px-4 py-3 text-xs text-slate-500">{u.email}</td>
                                <td className="px-4 py-3">
                                  <Badge className={`text-xs border-0 capitalize ${planColors[u.subscription_plan] || "bg-slate-100 text-slate-600"}`}>
                                    {u.subscription_plan === "full" ? "Full Access" : u.subscription_plan === "multi" ? "Multi-Function" : u.subscription_plan === "single" ? "Single Function" : u.subscription_plan}
                                  </Badge>
                                </td>
                                <td className="px-4 py-3 text-sm text-slate-700">{u.credits ?? 0}</td>
                                <td className="px-4 py-3">
                                  <button
                                    onClick={() => setCorpUserForm({ user_id: u.user_id, plan: u.subscription_plan, custom_credits: "" })}
                                    className="text-xs text-blue-600 hover:text-blue-800 border border-blue-100 hover:border-blue-300 px-2.5 py-1 rounded-lg transition-all"
                                    data-testid={`reprovision-user-btn-${u.user_id}`}
                                  >
                                    Change Plan
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Domain List */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3">
                  Whitelisted Domains ({corpDomains.length})
                </h3>
                {corpDomains.length === 0 ? (
                  <div className="text-center py-10 bg-white border border-dashed border-slate-200 rounded-xl">
                    <Globe size={28} className="text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-400">No domains added yet.</p>
                    <p className="text-xs text-slate-400">Add a domain above to auto-provision enterprise access.</p>
                  </div>
                ) : (
                  <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="text-left px-4 py-3 font-medium text-slate-600">Domain</th>
                          <th className="text-left px-4 py-3 font-medium text-slate-600">Plan</th>
                          <th className="text-left px-4 py-3 font-medium text-slate-600 hidden md:table-cell">Users Provisioned</th>
                          <th className="text-left px-4 py-3 font-medium text-slate-600 hidden lg:table-cell">Credits</th>
                          <th className="text-left px-4 py-3 font-medium text-slate-600 hidden xl:table-cell">Notes</th>
                          <th className="text-left px-4 py-3 font-medium text-slate-600">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {corpDomains.map(d => {
                          const fb = domainFeedback[d.domain_id];
                          const planColors = { full: "bg-blue-100 text-blue-700", multi: "bg-violet-100 text-violet-700", single: "bg-emerald-100 text-emerald-700", free: "bg-slate-100 text-slate-600" };
                          return (
                            <tr key={d.domain_id} className="hover:bg-slate-50" data-testid={`domain-row-${d.domain_id}`}>
                              <td className="px-4 py-3">
                                <span className="font-mono text-sm text-slate-900">@{d.domain}</span>
                              </td>
                              <td className="px-4 py-3">
                                <Badge className={`text-xs border-0 capitalize ${planColors[d.plan] || "bg-slate-100 text-slate-600"}`}>
                                  {d.plan === "full" ? "Full Access" : d.plan === "multi" ? "Multi-Function" : d.plan === "single" ? "Single Function" : d.plan}
                                </Badge>
                              </td>
                              <td className="px-4 py-3 hidden md:table-cell">
                                <span className="text-slate-700 font-medium">{d.provisioned_count ?? 0}</span>
                                <span className="text-slate-400 text-xs ml-1">user(s)</span>
                              </td>
                              <td className="px-4 py-3 hidden lg:table-cell text-xs text-slate-500">
                                {d.custom_credits != null
                                  ? <span className="font-medium text-blue-700">{d.custom_credits} credits <span className="text-slate-400 font-normal">(custom)</span></span>
                                  : <span className="text-slate-400">Plan default</span>}
                              </td>
                              <td className="px-4 py-3 hidden xl:table-cell text-xs text-slate-500">
                                {d.notes || "—"}
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  {fb === "applying" ? (
                                    <span className="text-xs text-slate-500 flex items-center gap-1"><Loader2 size={11} className="animate-spin" /> Applying...</span>
                                  ) : fb === "error" ? (
                                    <span className="text-xs text-red-500 flex items-center gap-1"><XCircle size={11} /> Failed</span>
                                  ) : fb ? (
                                    <span className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle size={11} /> {fb}</span>
                                  ) : (
                                    <button
                                      onClick={() => handleApplyDomain(d.domain_id)}
                                      className="inline-flex items-center gap-1 text-xs border border-slate-200 hover:border-blue-300 hover:text-blue-600 text-slate-500 px-2.5 py-1 rounded-lg font-medium transition-all bg-white"
                                      data-testid={`apply-domain-btn-${d.domain_id}`}
                                      title="Apply to existing users with this domain"
                                    >
                                      <RefreshCw size={11} /> Apply to existing
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleDeleteDomain(d.domain_id)}
                                    className="inline-flex items-center gap-1 text-xs border border-red-100 hover:border-red-300 text-red-400 hover:text-red-600 px-2 py-1 rounded-lg transition-all bg-white"
                                    data-testid={`delete-domain-btn-${d.domain_id}`}
                                  >
                                    <Trash2 size={11} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                <p className="text-xs text-slate-400 mt-2">
                  All functions are unlocked for enterprise users regardless of plan tier. Plan determines credit allocation.
                </p>
              </div>
            </div>
          </TabsContent>
          {/* Pricing Management */}
          <TabsContent value="pricing">
            <div className="space-y-8">
              <div>
                <h2 className="font-semibold text-slate-900 flex items-center gap-2 mb-1">
                  Pricing Management
                </h2>
                <p className="text-sm text-slate-500">Changes take effect immediately and persist across server restarts.</p>
              </div>

              {!pricingData ? (
                <div className="flex items-center gap-2 text-slate-400 text-sm"><Loader2 className="animate-spin w-4 h-4" /> Loading...</div>
              ) : (
                <>
                  {/* Subscription Plans */}
                  <div>
                    <h3 className="text-sm font-semibold text-slate-700 mb-3">Subscription Plans</h3>
                    <div className="grid sm:grid-cols-3 gap-4">
                      {Object.entries(pricingData.plans).filter(([k]) => k !== "free").map(([key, plan]) => (
                        <div key={key} className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`plan-card-${key}`}>
                          <div className="flex items-center justify-between mb-4">
                            <span className="font-semibold text-slate-900">{plan.name}</span>
                            <Badge className={`text-xs border-0 ${key === "full" ? "bg-blue-100 text-blue-700" : key === "multi" ? "bg-violet-100 text-violet-700" : "bg-emerald-100 text-emerald-700"}`}>
                              {key}
                            </Badge>
                          </div>
                          <div className="space-y-3">
                            <div>
                              <Label className="text-xs text-slate-500">Price (€ / month)</Label>
                              <div className="relative mt-1">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">€</span>
                                <Input
                                  type="number"
                                  min={0}
                                  step={0.01}
                                  value={pricingEdits.plans[key]?.price_eur ?? plan.price_eur}
                                  onChange={e => setPricingEdits(prev => ({
                                    ...prev,
                                    plans: { ...prev.plans, [key]: { ...prev.plans[key], price_eur: e.target.value } }
                                  }))}
                                  className="pl-7"
                                  data-testid={`plan-price-${key}`}
                                />
                              </div>
                            </div>
                            <div>
                              <Label className="text-xs text-slate-500">Credits included</Label>
                              <Input
                                type="number"
                                min={0}
                                value={pricingEdits.plans[key]?.credits ?? plan.credits}
                                onChange={e => setPricingEdits(prev => ({
                                  ...prev,
                                  plans: { ...prev.plans, [key]: { ...prev.plans[key], credits: e.target.value } }
                                }))}
                                className="mt-1"
                                data-testid={`plan-credits-${key}`}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Credit Packs */}
                  <div>
                    <h3 className="text-sm font-semibold text-slate-700 mb-3">Credit Packs</h3>
                    <div className="grid sm:grid-cols-3 gap-4">
                      {Object.entries(pricingData.credit_packs).map(([key, pack]) => (
                        <div key={key} className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`pack-card-${key}`}>
                          <p className="font-semibold text-slate-900 mb-4">{pack.credits} Credits Pack</p>
                          <div className="space-y-3">
                            <div>
                              <Label className="text-xs text-slate-500">Price (€)</Label>
                              <div className="relative mt-1">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">€</span>
                                <Input
                                  type="number"
                                  min={0}
                                  step={0.01}
                                  value={pricingEdits.credit_packs[key]?.amount ?? pack.amount}
                                  onChange={e => setPricingEdits(prev => ({
                                    ...prev,
                                    credit_packs: { ...prev.credit_packs, [key]: { ...prev.credit_packs[key], amount: e.target.value } }
                                  }))}
                                  className="pl-7"
                                  data-testid={`pack-price-${key}`}
                                />
                              </div>
                            </div>
                            <div>
                              <Label className="text-xs text-slate-500">Credits in pack</Label>
                              <Input
                                type="number"
                                min={0}
                                value={pricingEdits.credit_packs[key]?.credits ?? pack.credits}
                                onChange={e => setPricingEdits(prev => ({
                                  ...prev,
                                  credit_packs: { ...prev.credit_packs, [key]: { ...prev.credit_packs[key], credits: e.target.value } }
                                }))}
                                className="mt-1"
                                data-testid={`pack-credits-${key}`}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Pending scheduled change banner */}
                  {pricingData.pending && (
                    <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-5 py-4" data-testid="pending-pricing-banner">
                      <CalendarClock size={18} className="text-amber-600 mt-0.5 shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-amber-800">Pricing change scheduled</p>
                        <p className="text-xs text-amber-700 mt-0.5">
                          New pricing activates on{" "}
                          <span className="font-semibold">
                            {new Date(pricingData.pending.effective_at).toLocaleString(undefined, {
                              dateStyle: "medium", timeStyle: "short"
                            })} UTC
                          </span>
                          {pricingData.pending.created_by && ` · set by ${pricingData.pending.created_by}`}
                        </p>
                        {pricingData.pending.plans && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {Object.entries(pricingData.pending.plans).map(([k, v]) =>
                              v.price_eur !== undefined ? (
                                <span key={k} className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-mono">
                                  {k}: €{v.price_eur}
                                </span>
                              ) : null
                            )}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={handleCancelPending}
                        disabled={cancellingPending}
                        className="text-xs text-red-500 hover:text-red-700 border border-red-200 hover:border-red-400 px-3 py-1.5 rounded-lg transition-all whitespace-nowrap"
                        data-testid="cancel-pending-btn"
                      >
                        {cancellingPending ? <Loader2 size={11} className="animate-spin inline mr-1" /> : null}
                        Cancel
                      </button>
                    </div>
                  )}

                  {/* Schedule toggle + datetime picker */}
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 space-y-4">
                    <div className="flex items-center gap-4">
                      <button
                        onClick={() => setScheduleMode(false)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${!scheduleMode ? "bg-white border border-blue-200 text-blue-700 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                        data-testid="apply-now-btn"
                      >
                        <CheckCircle size={15} /> Apply immediately
                      </button>
                      <button
                        onClick={() => setScheduleMode(true)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${scheduleMode ? "bg-white border border-blue-200 text-blue-700 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                        data-testid="schedule-mode-btn"
                      >
                        <CalendarClock size={15} /> Schedule for later
                      </button>
                    </div>

                    {scheduleMode && (
                      <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                        <div className="flex-1">
                          <Label className="text-xs text-slate-600 mb-1 block">Activation Date &amp; Time <span className="text-slate-400">(UTC)</span></Label>
                          <input
                            type="datetime-local"
                            value={effectiveAt}
                            onChange={e => setEffectiveAt(e.target.value)}
                            min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
                            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            data-testid="effective-at-input"
                          />
                          <p className="text-xs text-slate-400 mt-1">Enter time in UTC. Pricing will activate automatically at this moment.</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Save */}
                  <div className="flex items-center gap-4 pt-1">
                    <Button
                      onClick={handleSavePricing}
                      disabled={pricingSaving || (scheduleMode && !effectiveAt)}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-8"
                      data-testid="save-pricing-btn"
                    >
                      {pricingSaving ? <Loader2 className="animate-spin w-4 h-4 mr-2" /> : scheduleMode ? <CalendarClock size={15} className="mr-2" /> : null}
                      {scheduleMode ? "Schedule Pricing" : "Save Pricing"}
                    </Button>
                    {pricingSuccess === "saved" && (
                      <span className="flex items-center gap-1.5 text-sm text-emerald-600" data-testid="pricing-saved-msg">
                        <CheckCircle size={15} /> Pricing updated live
                      </span>
                    )}
                    {pricingSuccess === "scheduled" && (
                      <span className="flex items-center gap-1.5 text-sm text-amber-600" data-testid="pricing-scheduled-msg">
                        <CalendarClock size={15} /> Pricing scheduled successfully
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-400">
                    Existing active subscriptions are unaffected. New checkouts use the updated prices.
                  </p>
                </>
              )}
            </div>
          </TabsContent>

          {/* Contributors */}
          <TabsContent value="contributors">
            <div className="space-y-6">
              <div>
                <h2 className="font-semibold text-slate-900 flex items-center gap-2 mb-1">
                  <Award size={16} className="text-violet-600" /> Contributor Management
                </h2>
                <p className="text-sm text-slate-500">Manage contributor profiles and recognition. The Promptlee platform profile cannot be deleted.</p>
              </div>

              {/* Add contributor form */}
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-slate-900 mb-4">Add New Contributor</h3>
                {contribError && <Alert variant="destructive" className="mb-3"><AlertDescription>{contribError}</AlertDescription></Alert>}
                {contribUsers.length === 0 ? (
                  <p className="text-sm text-slate-400">No eligible users available. Users must register first.</p>
                ) : (
                  <form onSubmit={handleCreateContributor} className="space-y-3">
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div>
                        <Label>Select User</Label>
                        <Select value={contribForm.user_id} onValueChange={v => {
                          const u = contribUsers.find(u => u.user_id === v);
                          setContribForm(f => ({ ...f, user_id: v, name: u?.name || "" }));
                        }}>
                          <SelectTrigger className="mt-1" data-testid="contrib-user-select">
                            <SelectValue placeholder="Choose a user..." />
                          </SelectTrigger>
                          <SelectContent>
                            {contribUsers.map(u => (
                              <SelectItem key={u.user_id} value={u.user_id}>{u.name} — {u.email}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Display Name</Label>
                        <Input value={contribForm.name} onChange={e => setContribForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Jane Doe" className="mt-1" data-testid="contrib-name-input" />
                      </div>
                      <div>
                        <Label>Title / Expertise</Label>
                        <Input value={contribForm.title} onChange={e => setContribForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Procurement Expert" className="mt-1" data-testid="contrib-title-input" />
                      </div>
                      <div>
                        <Label>Bio (optional)</Label>
                        <Input value={contribForm.bio} onChange={e => setContribForm(f => ({ ...f, bio: e.target.value }))} placeholder="Short description..." className="mt-1" data-testid="contrib-bio-input" />
                      </div>
                    </div>
                    <Button type="submit" disabled={contribSaving || !contribForm.user_id} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-contributor-btn">
                      {contribSaving ? <Loader2 className="animate-spin w-4 h-4 mr-1.5" /> : <Plus size={15} className="mr-1.5" />}
                      Add Contributor
                    </Button>
                  </form>
                )}
              </div>

              {/* Contributors list */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3">All Contributors ({contributors.length})</h3>
                <div className="space-y-3">
                  {contributors.map(c => (
                    <div key={c.contributor_id} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-4" data-testid={`contrib-row-${c.contributor_id}`}>
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-base font-bold shrink-0 ${c.is_platform_author ? "bg-blue-600 text-white" : "bg-blue-100 text-blue-700"}`}>
                        {c.is_platform_author ? "W" : (c.name?.[0] || "C").toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Link to={`/contributor/${c.contributor_id}`} className="font-semibold text-sm text-slate-900 hover:text-blue-600 transition-colors">
                            {c.name}
                          </Link>
                          {c.is_platform_author && <Badge className="text-xs bg-blue-100 text-blue-700 border-0"><Shield size={9} className="mr-1 inline" />Platform</Badge>}
                          {(c.badges || []).includes("top_contributor") && <Badge className="text-xs bg-violet-100 text-violet-700 border-0"><Award size={9} className="mr-1 inline" />Top</Badge>}
                          {(c.badges || []).includes("highly_rated") && <Badge className="text-xs bg-amber-100 text-amber-700 border-0"><Star size={9} className="mr-1 inline fill-amber-500" />Rated</Badge>}
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">{c.title || "No title"} · {c.contribution_count} apps · {c.rating_count} ratings {c.overall_rating > 0 ? `· ★ ${c.overall_rating}` : ""}</p>
                      </div>
                      {!c.is_platform_author && (
                        <button onClick={() => handleDeleteContributor(c.contributor_id)} className="text-red-400 hover:text-red-600 p-1" data-testid={`delete-contrib-${c.contributor_id}`}>
                          <Trash2 size={15} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Subcategories */}
          <TabsContent value="subcategories">
            <div className="space-y-6">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h2 className="font-semibold text-slate-900 flex items-center gap-2 mb-1"><Layers size={15} className="text-blue-600" /> Subcategory Manager</h2>
                  <p className="text-sm text-slate-500">Organise applications into subcategories under each function.</p>
                </div>
                <Select value={subcatFilterFn || "all"} onValueChange={v => setSubcatFilterFn(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-44" data-testid="subcat-filter-fn"><SelectValue placeholder="All Functions" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Functions</SelectItem>
                    {FUNCTIONS.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {/* Subcategory form */}
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-slate-900 mb-4">
                  {editSubcat ? `Edit: ${editSubcat.name}` : "Add New Subcategory"}
                </h3>
                {subcatError && <Alert variant="destructive" className="mb-3"><AlertDescription>{subcatError}</AlertDescription></Alert>}
                <form onSubmit={handleSaveSubcat} className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Function</Label>
                    <Select value={subcatForm.function_id} onValueChange={v => setSubcatForm(f => ({ ...f, function_id: v }))}>
                      <SelectTrigger className="mt-1" data-testid="subcat-form-function"><SelectValue placeholder="Select function" /></SelectTrigger>
                      <SelectContent>{FUNCTIONS.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Name</Label>
                    <Input value={subcatForm.name} onChange={e => setSubcatForm(f => ({ ...f, name: e.target.value }))} className="mt-1" placeholder="e.g. Vendor Management" data-testid="subcat-form-name" required />
                  </div>
                  <div className="sm:col-span-2">
                    <Label>Description</Label>
                    <Input value={subcatForm.description} onChange={e => setSubcatForm(f => ({ ...f, description: e.target.value }))} className="mt-1" placeholder="Short description of this subcategory" data-testid="subcat-form-description" />
                  </div>
                  <div className="sm:col-span-2 flex gap-2">
                    <Button type="submit" disabled={subcatSaving || !subcatForm.function_id || !subcatForm.name} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-subcat-btn">
                      {subcatSaving ? <Loader2 className="animate-spin w-4 h-4 mr-1" /> : null}
                      {editSubcat ? "Save Changes" : "Add Subcategory"}
                    </Button>
                    {editSubcat && <Button type="button" variant="outline" onClick={() => { setEditSubcat(null); setSubcatForm({ function_id: "", name: "", description: "", order: 0 }); }}>Cancel</Button>}
                  </div>
                </form>
              </div>

              {/* Subcategory list grouped by function */}
              {FUNCTIONS.filter(f => !subcatFilterFn || f.id === subcatFilterFn).map(fn => {
                const fnSubcats = subcategories.filter(s => s.function_id === fn.id);
                if (fnSubcats.length === 0 && subcatFilterFn) return null;
                return (
                  <div key={fn.id}>
                    <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                      <span className="capitalize">{fn.name}</span>
                      <Badge className="text-xs border-0 bg-slate-100 text-slate-500">{fnSubcats.length}</Badge>
                    </h3>
                    {fnSubcats.length === 0 ? (
                      <p className="text-xs text-slate-400 pl-1">No subcategories yet.</p>
                    ) : (
                      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50 border-b"><tr>
                            <th className="text-left px-4 py-2.5 font-medium text-slate-600 text-xs">Name</th>
                            <th className="text-left px-4 py-2.5 font-medium text-slate-600 text-xs hidden sm:table-cell">Description</th>
                            <th className="text-left px-4 py-2.5 font-medium text-slate-600 text-xs">Apps</th>
                            <th className="px-4 py-2.5"></th>
                          </tr></thead>
                          <tbody className="divide-y divide-slate-100">
                            {fnSubcats.map(s => (
                              <tr key={s.subcategory_id} className="hover:bg-slate-50" data-testid={`subcat-row-${s.subcategory_id}`}>
                                <td className="px-4 py-3 font-medium text-slate-900 text-sm">{s.name}</td>
                                <td className="px-4 py-3 text-xs text-slate-500 hidden sm:table-cell max-w-xs truncate">{s.description}</td>
                                <td className="px-4 py-3 text-sm text-slate-700">{s.app_count ?? 0}</td>
                                <td className="px-4 py-3 flex items-center gap-2 justify-end">
                                  <button onClick={() => { setEditSubcat(s); setSubcatForm({ function_id: s.function_id, name: s.name, description: s.description, order: s.order || 0 }); }} className="text-xs text-blue-600 hover:text-blue-800" data-testid={`edit-subcat-${s.subcategory_id}`}>Edit</button>
                                  <button onClick={() => handleDeleteSubcat(s.subcategory_id)} className="text-xs text-red-500 hover:text-red-700" data-testid={`delete-subcat-${s.subcategory_id}`}>Delete</button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </TabsContent>

          {/* Bulk Upload */}
          <TabsContent value="bulk-upload">
            <BulkUploadSection API={API} />
          </TabsContent>

          {/* Gift Codes */}
          <TabsContent value="gift-codes">
            <GiftCodesAdmin API={API} />
          </TabsContent>

          {/* Referrals / Promotions */}
          <TabsContent value="promotions">
            <ReferralsAdmin API={API} />
          </TabsContent>

          {/* Analytics */}
          <TabsContent value="analytics">
            <div className="space-y-6">
              {/* Header + period toggle */}
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h2 className="font-semibold text-slate-900 flex items-center gap-2 mb-1">
                    <BarChart2 size={16} className="text-blue-600" /> Analytics Dashboard
                  </h2>
                  <p className="text-sm text-slate-500">Platform performance and growth metrics.</p>
                </div>
                <div className="flex items-center gap-2">
                  {[7, 30, 90].map(d => (
                    <button
                      key={d}
                      onClick={() => { setAnalyticsDays(d); fetchAnalytics(d); }}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${analyticsDays === d ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"}`}
                      data-testid={`analytics-days-${d}`}
                    >
                      Last {d}d
                    </button>
                  ))}
                  <button
                    onClick={() => fetchAnalytics(analyticsDays)}
                    className="p-1.5 rounded-lg border border-slate-200 hover:border-blue-300 text-slate-500 hover:text-blue-600 transition-all"
                    title="Refresh"
                    data-testid="analytics-refresh-btn"
                  >
                    <RefreshCw size={14} />
                  </button>
                </div>
              </div>

              {analyticsLoading ? (
                <div className="flex items-center justify-center py-16 text-slate-400 gap-2">
                  <Loader2 className="animate-spin w-5 h-5" /> Loading analytics...
                </div>
              ) : !analytics ? (
                <div className="text-center py-16">
                  <Activity size={32} className="text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-400 text-sm">No data yet.</p>
                </div>
              ) : (
                <>
                  {/* KPI Cards */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="analytics-kpi-cards">
                    <div className="bg-white border border-slate-200 rounded-xl p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                          <TrendingUp size={15} className="text-blue-600" />
                        </div>
                        <span className="text-xs text-slate-500 font-medium">New Users (7d)</span>
                      </div>
                      <p className="text-3xl font-bold text-slate-900" data-testid="kpi-new-users-7d">{analytics.new_users_7d}</p>
                      <p className="text-xs text-slate-400 mt-1">{analytics.new_users_30d} in last 30d</p>
                    </div>
                    <div className="bg-white border border-slate-200 rounded-xl p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                          <DollarSign size={15} className="text-emerald-600" />
                        </div>
                        <span className="text-xs text-slate-500 font-medium">Revenue ({analyticsDays}d)</span>
                      </div>
                      <p className="text-3xl font-bold text-slate-900" data-testid="kpi-revenue">€{analytics.recent_revenue.toFixed(2)}</p>
                      <p className="text-xs text-slate-400 mt-1">€{analytics.total_revenue.toFixed(2)} all time</p>
                    </div>
                    <div className="bg-white border border-slate-200 rounded-xl p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center">
                          <LayoutGrid size={15} className="text-violet-600" />
                        </div>
                        <span className="text-xs text-slate-500 font-medium">Active Apps</span>
                      </div>
                      <p className="text-3xl font-bold text-slate-900">{analytics.free_apps + analytics.premium_apps}</p>
                      <p className="text-xs text-slate-400 mt-1">{analytics.premium_apps} premium · {analytics.free_apps} free</p>
                    </div>
                    <div className="bg-white border border-slate-200 rounded-xl p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                          <Star size={15} className="text-amber-600" />
                        </div>
                        <span className="text-xs text-slate-500 font-medium">Avg Rating</span>
                      </div>
                      <p className="text-3xl font-bold text-slate-900">{analytics.avg_rating > 0 ? analytics.avg_rating.toFixed(1) : "—"}</p>
                      <p className="text-xs text-slate-400 mt-1">{analytics.total_reviews} review{analytics.total_reviews !== 1 ? "s" : ""}</p>
                    </div>
                  </div>

                  {/* Signup Trend Chart */}
                  <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="signup-trend-chart">
                    <h3 className="text-sm font-semibold text-slate-900 mb-4">User Signups — Last {analyticsDays} Days</h3>
                    <ResponsiveContainer width="100%" height={220}>
                      <LineChart data={analytics.signup_trend} margin={{ top: 4, right: 16, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis
                          dataKey="date"
                          tick={{ fontSize: 10, fill: "#94a3b8" }}
                          tickFormatter={v => v.slice(5)}
                          interval={Math.floor(analyticsDays / 6)}
                        />
                        <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} allowDecimals={false} />
                        <Tooltip
                          contentStyle={{ fontSize: 12, border: "1px solid #e2e8f0", borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,.06)" }}
                          labelFormatter={v => `Date: ${v}`}
                        />
                        <Line type="monotone" dataKey="signups" stroke="#2563eb" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    {/* Plan Distribution */}
                    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="plan-distribution-chart">
                      <h3 className="text-sm font-semibold text-slate-900 mb-4">User Plan Distribution</h3>
                      {(() => {
                        const PLAN_COLORS = { free: "#94a3b8", single: "#10b981", multi: "#8b5cf6", full: "#2563eb" };
                        const PLAN_LABELS = { free: "Free", single: "Single", multi: "Multi", full: "Full Access" };
                        const pieData = Object.entries(analytics.plan_distribution).map(([key, val]) => ({
                          name: PLAN_LABELS[key] || key, value: val, color: PLAN_COLORS[key] || "#64748b"
                        }));
                        const total = pieData.reduce((s, d) => s + d.value, 0);
                        return (
                          <div className="flex items-center gap-6">
                            <ResponsiveContainer width={160} height={160}>
                              <PieChart>
                                <Pie data={pieData} cx={75} cy={75} innerRadius={45} outerRadius={70} dataKey="value" strokeWidth={2}>
                                  {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                                </Pie>
                                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 6 }} />
                              </PieChart>
                            </ResponsiveContainer>
                            <div className="flex-1 space-y-2">
                              {pieData.map(item => (
                                <div key={item.name} className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <div className="w-2.5 h-2.5 rounded-full" style={{ background: item.color }} />
                                    <span className="text-xs text-slate-600">{item.name}</span>
                                  </div>
                                  <div className="text-right">
                                    <span className="text-xs font-semibold text-slate-900">{item.value}</span>
                                    <span className="text-xs text-slate-400 ml-1">({total > 0 ? Math.round(item.value / total * 100) : 0}%)</span>
                                  </div>
                                </div>
                              ))}
                              <p className="text-xs text-slate-400 pt-1">{total} total users</p>
                            </div>
                          </div>
                        );
                      })()}
                    </div>

                    {/* Apps by Function */}
                    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="apps-by-function-chart">
                      <h3 className="text-sm font-semibold text-slate-900 mb-4">Apps by Function</h3>
                      {(() => {
                        const barData = Object.entries(analytics.apps_by_function)
                          .map(([key, val]) => ({ name: key.charAt(0).toUpperCase() + key.slice(1), apps: val }))
                          .sort((a, b) => b.apps - a.apps);
                        return (
                          <ResponsiveContainer width="100%" height={160}>
                            <BarChart data={barData} margin={{ top: 0, right: 8, left: -25, bottom: 0 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                              <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#94a3b8" }} />
                              <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} allowDecimals={false} />
                              <Tooltip contentStyle={{ fontSize: 11, borderRadius: 6 }} />
                              <Bar dataKey="apps" fill="#2563eb" radius={[4, 4, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Revenue Trend */}
                  {analytics.total_paid_transactions > 0 && (
                    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="revenue-trend-chart">
                      <h3 className="text-sm font-semibold text-slate-900 mb-4">Revenue — Last {analyticsDays} Days (€)</h3>
                      <ResponsiveContainer width="100%" height={180}>
                        <BarChart data={analytics.revenue_trend} margin={{ top: 4, right: 16, left: -20, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                          <XAxis
                            dataKey="date"
                            tick={{ fontSize: 10, fill: "#94a3b8" }}
                            tickFormatter={v => v.slice(5)}
                            interval={Math.floor(analyticsDays / 6)}
                          />
                          <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} />
                          <Tooltip
                            contentStyle={{ fontSize: 12, borderRadius: 8 }}
                            formatter={v => [`€${v}`, "Revenue"]}
                            labelFormatter={v => `Date: ${v}`}
                          />
                          <Bar dataKey="revenue" fill="#10b981" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {/* Top Apps */}
                  {analytics.top_apps.length > 0 && (
                    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="top-apps-table">
                      <h3 className="text-sm font-semibold text-slate-900 mb-4">Top Apps by Favourites</h3>
                      <div className="space-y-2">
                        {analytics.top_apps.map((app, i) => (
                          <div key={app.app_id} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                            <div className="flex items-center gap-3">
                              <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-500 text-xs flex items-center justify-center font-semibold">{i + 1}</span>
                              <div>
                                <p className="text-sm font-medium text-slate-900">{app.title}</p>
                                <p className="text-xs text-slate-400 capitalize">{app.function_id}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4 text-right">
                              <div>
                                <p className="text-sm font-semibold text-slate-900">{app.favourite_count}</p>
                                <p className="text-xs text-slate-400">favs</p>
                              </div>
                              {app.avg_rating > 0 && (
                                <div>
                                  <p className="text-sm font-semibold text-amber-600">★ {app.avg_rating}</p>
                                  <p className="text-xs text-slate-400">{app.review_count} reviews</p>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Verification health */}
                  <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="verification-health">
                    <h3 className="text-sm font-semibold text-slate-900 mb-3">Email Verification Health</h3>
                    <div className="flex items-center gap-4">
                      <div className="flex-1 bg-slate-100 rounded-full h-3 overflow-hidden">
                        <div
                          className="h-full bg-emerald-500 rounded-full transition-all"
                          style={{ width: `${analytics.verified_count + analytics.unverified_count > 0 ? Math.round(analytics.verified_count / (analytics.verified_count + analytics.unverified_count) * 100) : 0}%` }}
                        />
                      </div>
                      <div className="flex items-center gap-3 text-sm shrink-0">
                        <span className="flex items-center gap-1.5 text-emerald-700 font-medium">
                          <CheckCircle size={13} /> {analytics.verified_count} verified
                        </span>
                        {analytics.unverified_count > 0 && (
                          <span className="flex items-center gap-1.5 text-amber-600">
                            <Clock size={13} /> {analytics.unverified_count} pending
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* ── Application Performance Table ─────────────────────────── */}
              <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="app-analytics-section">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                  <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                    <Activity size={15} className="text-blue-600" /> Application Performance
                  </h3>
                  <div className="flex items-center gap-2 flex-wrap">
                    <input
                      type="text"
                      placeholder="Search apps..."
                      value={appAnalyticsSearch}
                      onChange={e => setAppAnalyticsSearch(e.target.value)}
                      className="border border-slate-200 rounded-lg px-3 py-1.5 text-xs w-36 focus:outline-none focus:ring-1 focus:ring-blue-400"
                      data-testid="app-analytics-search"
                    />
                    <select
                      value={appAnalyticsFn}
                      onChange={e => { setAppAnalyticsFn(e.target.value); fetchAppAnalytics(e.target.value, appAnalyticsSort); }}
                      className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
                      data-testid="app-analytics-fn-filter"
                    >
                      <option value="">All Functions</option>
                      {["procurement","finance","marketing","hr","sales","operations","general"].map(fn => (
                        <option key={fn} value={fn}>{fn.charAt(0).toUpperCase() + fn.slice(1)}</option>
                      ))}
                    </select>
                    <select
                      value={appAnalyticsSort}
                      onChange={e => { setAppAnalyticsSort(e.target.value); fetchAppAnalytics(appAnalyticsFn, e.target.value); }}
                      className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
                      data-testid="app-analytics-sort"
                    >
                      <option value="view_count">Sort: Views</option>
                      <option value="copy_count">Sort: Copies</option>
                      <option value="avg_rating">Sort: Rating</option>
                      <option value="review_count">Sort: Reviews</option>
                    </select>
                    <button
                      onClick={() => fetchAppAnalytics(appAnalyticsFn, appAnalyticsSort)}
                      className="p-1.5 rounded-lg border border-slate-200 hover:border-blue-300 text-slate-500 hover:text-blue-600 transition-all"
                      title="Refresh"
                      data-testid="app-analytics-refresh"
                    >
                      <RefreshCw size={14} />
                    </button>
                  </div>
                </div>

                {appAnalyticsLoading ? (
                  <div className="flex items-center justify-center py-10 text-slate-400 gap-2">
                    <Loader2 className="animate-spin w-4 h-4" /> Loading...
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs" data-testid="app-analytics-table">
                      <thead>
                        <tr className="border-b border-slate-100">
                          <th className="text-left py-2 pr-3 text-slate-500 font-medium w-8">#</th>
                          <th className="text-left py-2 pr-3 text-slate-500 font-medium">App</th>
                          <th className="text-left py-2 pr-3 text-slate-500 font-medium hidden sm:table-cell">Function</th>
                          <th className="text-right py-2 pr-3 text-slate-500 font-medium">Views</th>
                          <th className="text-right py-2 pr-3 text-slate-500 font-medium">Copies</th>
                          <th className="text-right py-2 pr-3 text-slate-500 font-medium hidden md:table-cell">Copy Rate</th>
                          <th className="text-right py-2 pr-3 text-slate-500 font-medium hidden md:table-cell">Rating</th>
                          <th className="text-right py-2 text-slate-500 font-medium hidden lg:table-cell">Favs</th>
                        </tr>
                      </thead>
                      <tbody>
                        {appAnalytics
                          .filter(a => !appAnalyticsSearch || a.title.toLowerCase().includes(appAnalyticsSearch.toLowerCase()))
                          .map((a, i) => (
                          <tr key={a.app_id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors" data-testid={`app-analytics-row-${a.app_id}`}>
                            <td className="py-2.5 pr-3 text-slate-400">{i + 1}</td>
                            <td className="py-2.5 pr-3">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-slate-800 leading-tight">{a.title}</span>
                                {a.is_premium && (
                                  <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-[10px] font-medium shrink-0">PRO</span>
                                )}
                              </div>
                            </td>
                            <td className="py-2.5 pr-3 text-slate-500 capitalize hidden sm:table-cell">{a.function_id}</td>
                            <td className="py-2.5 pr-3 text-right font-semibold text-slate-800">{a.view_count.toLocaleString()}</td>
                            <td className="py-2.5 pr-3 text-right text-slate-600">{a.copy_count.toLocaleString()}</td>
                            <td className="py-2.5 pr-3 text-right hidden md:table-cell">
                              <span className={`font-medium ${a.copy_rate >= 20 ? "text-emerald-600" : a.copy_rate >= 5 ? "text-amber-600" : "text-slate-400"}`}>
                                {a.copy_rate > 0 ? `${a.copy_rate}%` : "—"}
                              </span>
                            </td>
                            <td className="py-2.5 pr-3 text-right hidden md:table-cell">
                              {a.avg_rating > 0 ? (
                                <span className="text-amber-600 font-medium">★ {a.avg_rating.toFixed(1)}</span>
                              ) : <span className="text-slate-300">—</span>}
                            </td>
                            <td className="py-2.5 text-right text-slate-500 hidden lg:table-cell">{a.favourite_count > 0 ? a.favourite_count : "—"}</td>
                          </tr>
                        ))}
                        {appAnalytics.filter(a => !appAnalyticsSearch || a.title.toLowerCase().includes(appAnalyticsSearch.toLowerCase())).length === 0 && (
                          <tr>
                            <td colSpan={8} className="py-8 text-center text-slate-400">No apps found.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

        </Tabs>
      </div>

      {/* Shared confirmation dialog */}
      <AlertDialog
        open={confirmState.open}
        onOpenChange={open => !open && setConfirmState({ open: false, message: "", onConfirm: null })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>{confirmState.message}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConfirmState({ open: false, message: "", onConfirm: null })}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => {
                confirmState.onConfirm?.();
                setConfirmState({ open: false, message: "", onConfirm: null });
              }}
              data-testid="confirm-action-btn"
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminPage;
